// Create / edit a delivery schedule for one report — emailed, or uploaded to an SFTP destination.
import { useEffect, useState } from "react";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ParamFields } from "./ParamFields";
import { RecipientsField } from "./RecipientsField";
import { ChannelToggle, DestinationSelect, useDestinations } from "./DeliveryTarget";
import { DOW, FREQ_LABEL, defaultParams, formatLabel } from "./reportsApi";

const TZS = ["America/Edmonton", "America/Vancouver", "America/Toronto", "America/Halifax", "America/Winnipeg", "UTC"];
const CRON_EXAMPLES = [["0 7 * * 1-5", "weekdays at 07:00"], ["*/30 8-17 * * 1-5", "every 30 min, business hours"],
  ["0 6 1,15 * *", "1st and 15th at 06:00"], ["0 22 * * 0", "Sundays at 22:00"]];
export const SCHEDULE_META_KEYS = ["schedule_id", "owner_user_id", "owner_name", "created_by", "created_at", "updated_at",
  "next_run_at", "last_run_at", "last_status", "last_error", "last_rows", "run_count", "legacy_report_id", "report_label",
  "schedule_label", "mine", "owner_active", "destination_name", "handoff"];

function CronField({ value, tz, onChange }) {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!value.trim()) { setPreview(null); return undefined; }
    const t = setTimeout(() => {
      api.post("/report-hub/schedules/cron-preview", { cron_expr: value, tz }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(t);
  }, [value, tz]);
  return (
    <div className="space-y-1.5 sm:col-span-3" data-testid="schedule-cron-field">
      <Label className="text-[11px] text-slate-500">Cron expression <span className="font-normal text-slate-400">— minute hour day-of-month month day-of-week, in the timezone chosen</span></Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-9 font-mono" placeholder="0 7 * * 1-5" data-testid="schedule-cron" />
      <div className="flex flex-wrap gap-1">
        {CRON_EXAMPLES.map(([expr, label]) => (
          <button key={expr} type="button" onClick={() => onChange(expr)} data-testid={`schedule-cron-example-${expr.replace(/[^0-9a-z]/gi, "")}`}
                  className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-600 hover:bg-slate-100">
            <span className="font-mono">{expr}</span> · {label}
          </button>
        ))}
      </div>
      {preview && (
        <p className={`text-[11px] ${preview.ok ? "text-slate-500" : "text-rose-600"}`} data-testid="schedule-cron-preview">
          {preview.ok ? <>Next: {preview.next_runs.slice(0, 3).map((d) => new Date(d).toLocaleString()).join(" · ")}</> : preview.error}
        </p>
      )}
    </div>
  );
}

function blank(report, params, channel) {
  return {
    report_key: report.key, name: report.label, params: params || defaultParams(report),
    format: report.formats.includes("pdf") ? "pdf" : report.formats[0], recipients: [], send_to_self: true,
    channel: channel || "email", destination_id: "",
    frequency: "weekly", cron_expr: "", day_of_week: "mon", day_of_month: 1, hour: 9, minute: 0,
    tz: "America/Edmonton", enabled: true, skip_empty: true, note: "",
  };
}

export function ScheduleDialog({ report, params, schedule, open, onClose, channel }) {
  const { user } = useAuth();
  const [form, setForm] = useState(() => (schedule ? { channel: "email", destination_id: "", cron_expr: "", ...schedule } : blank(report, params, channel)));
  const [busy, setBusy] = useState(false);
  const destinations = useDestinations(true);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const time = `${String(form.hour).padStart(2, "0")}:${String(form.minute).padStart(2, "0")}`;
  const sftp = form.channel === "sftp";
  const formats = sftp ? report.formats.filter((f) => f === "csv" || f === "pdf") : report.formats;
  const fmt = formats.includes(form.format) ? form.format : formats[0];

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, format: fmt, day_of_month: parseInt(form.day_of_month, 10) || 1 };
      SCHEDULE_META_KEYS.forEach((k) => delete body[k]);
      const { data } = schedule?.schedule_id
        ? await api.put(`/report-hub/schedules/${schedule.schedule_id}`, body)
        : await api.post("/report-hub/schedules", body);
      toast.success(`Schedule saved — runs ${data.schedule_label}`);
      onClose(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save the schedule");
    } finally { setBusy(false); }
  };

  const canSave = !busy && formats.length > 0 && (form.frequency !== "cron" || !!(form.cron_expr || "").trim())
    && (sftp ? !!form.destination_id : (form.recipients.length > 0 || form.send_to_self));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle>{schedule?.schedule_id ? "Edit schedule" : "Schedule this report"}</DialogTitle>
          <DialogDescription>
            {report.label} — {sftp ? "uploaded to an SFTP destination" : "emailed"} automatically on the cadence you choose, in your data scope.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <ChannelToggle value={form.channel} onChange={(v) => set("channel", v)} testid="schedule-channel" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Schedule name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="h-9" data-testid="schedule-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{sftp ? "File" : "Delivery"}</Label>
              <Select value={fmt || ""} onValueChange={(v) => set("format", v)}>
                <SelectTrigger className="h-9" data-testid="schedule-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {formats.map((f) => <SelectItem key={f} value={f} data-testid={`schedule-format-${f}`}>{formatLabel(f, form.channel)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Report parameters</div>
            <ParamFields report={report} params={form.params} onChange={(p) => set("params", p)} compact />
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">When</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] text-slate-500">Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
                  <SelectTrigger className="h-9" data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(FREQ_LABEL).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`schedule-frequency-${v}`}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.frequency === "weekly" && (
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-500">Day</Label>
                  <Select value={form.day_of_week} onValueChange={(v) => set("day_of_week", v)}>
                    <SelectTrigger className="h-9" data-testid="schedule-dow"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOW.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {form.frequency === "monthly" && (
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-500">Day of month (1–28)</Label>
                  <Input type="number" min={1} max={28} className="h-9" value={form.day_of_month}
                         onChange={(e) => set("day_of_month", e.target.value)} data-testid="schedule-dom" />
                </div>
              )}
              {form.frequency === "cron" ? null : form.frequency === "hourly" ? (
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-500">Minute past each hour</Label>
                  <Input type="number" min={0} max={59} className="h-9" value={form.minute} data-testid="schedule-minute"
                         onChange={(e) => set("minute", Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)))} />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-500">Time</Label>
                  <Input type="time" className="h-9" value={time} data-testid="schedule-time"
                         onChange={(e) => { const [h, m] = e.target.value.split(":").map((n) => parseInt(n, 10)); setForm((f) => ({ ...f, hour: isNaN(h) ? 9 : h, minute: isNaN(m) ? 0 : m })); }} />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-[11px] text-slate-500">Timezone</Label>
                <Select value={form.tz} onValueChange={(v) => set("tz", v)}>
                  <SelectTrigger className="h-9" data-testid="schedule-tz"><SelectValue /></SelectTrigger>
                  <SelectContent>{TZS.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.frequency === "cron" && <CronField value={form.cron_expr || ""} tz={form.tz} onChange={(v) => set("cron_expr", v)} />}
            </div>
          </div>

          {sftp ? (
            <DestinationSelect value={form.destination_id} onChange={(v) => set("destination_id", v)} destinations={destinations} testid="schedule-destination" />
          ) : (
            <RecipientsField value={form.recipients} onChange={(v) => set("recipients", v)} sendToSelf={form.send_to_self}
                             onSendToSelf={(v) => set("send_to_self", v)} myEmail={user?.email} testid="schedule-recipients" />
          )}

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="schedule-enabled" /> Enabled
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch checked={form.skip_empty} onCheckedChange={(v) => set("skip_empty", v)} data-testid="schedule-skip-empty" />
              {sftp ? "Skip the upload when there are no rows" : "Skip the email when there are no rows"}
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(null)} data-testid="schedule-cancel">Cancel</Button>
          <Button onClick={save} disabled={!canSave} data-testid="schedule-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Save schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
